// One prize table against another, row by row — t-159, the per-row diff of
// .codelane/artifacts/t-159/leaderboard-pending-rewards.html (947-983).
//
// A scheduled prize table is a FULL REPLACEMENT: the package carries the table
// the next cut will install, not a list of edits to the one on the board. So
// the only way to say what changed is to read the two tables against each
// other — and the only thing a row can be matched by is its RANGE. A reward
// has no key of its own that outlives an edit: the id belongs to the row on
// the board, and a replacement built in the browser has none to offer.
//
// Which makes the range the identity, and that is not a compromise: two rows
// paying the same range would be two prizes for one placement, so a range
// names at most one row of a table. The tier and the basis join it, because
// "1 – 1" in Gold and "1 – 1" in Silver are two different placements.

import { rewardPayloadText } from "./rewardPayload";
import type { RewardSpec } from "./types";

export type RewardChange = "added" | "removed" | "changed";

export interface RewardDiffRow {
  change: RewardChange;
  /** The row's identity, and what the Range column prints. */
  range: string;
  /** The board's payload — on `removed` and `changed` rows. */
  oldPayload?: string;
  /** The replacement's payload — on `added` and `changed` rows. */
  newPayload?: string;
  note?: string;
  /** The identity the row was matched by. Stable across renders, so it is
     also the React key. */
  key: string;
}

export interface RewardsTableDiff {
  rows: RewardDiffRow[];
  added: number;
  removed: number;
  changed: number;
  beforeCount: number;
  afterCount: number;
}

function isNum(v: number | undefined | null): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

/** What the Range column prints. "top 1%" where a percentile row starts at
   zero — the mockup's own wording, and the only reading of a band that opens
   at the very top. */
export function rewardRangeLabel(r: RewardSpec): string {
  if (isNum(r.rank_from) && isNum(r.rank_to)) {
    return `${r.rank_from} – ${r.rank_to}`;
  }
  if (isNum(r.percentile_from) && isNum(r.percentile_to)) {
    return r.percentile_from === 0
      ? `top ${r.percentile_to}%`
      : `${r.percentile_from} – ${r.percentile_to}%`;
  }
  // Neither range: a claim_gated ladder rung, where the tier IS the range.
  return r.tier_key ? `tier ${r.tier_key}` : "—";
}

/**
 * The identity a row is matched by. The basis and the tier are part of it
 * because they say WHICH ranking the range is a slice of — the same numbers
 * inside two leagues are two placements, not one moved prize.
 */
export function rewardIdentity(r: RewardSpec): string {
  const basis = r.rank_basis ?? "entity";
  const tier = r.tier_key ?? "";
  if (isNum(r.rank_from) && isNum(r.rank_to)) {
    return `${basis}|${tier}|rank:${r.rank_from}-${r.rank_to}`;
  }
  if (isNum(r.percentile_from) && isNum(r.percentile_to)) {
    return `${basis}|${tier}|pct:${r.percentile_from}-${r.percentile_to}`;
  }
  return `${basis}|${tier}|threshold`;
}

/**
 * JSON with every object's keys sorted, so a payload that came off the wire
 * and the same payload retyped in the editor compare equal. Without it every
 * scheduled table would report rows as changed for having been serialised in
 * a different order.
 */
export function stablePayload(v: unknown): string {
  if (v === null || typeof v !== "object") return JSON.stringify(v ?? null) ?? "null";
  if (Array.isArray(v)) return `[${v.map(stablePayload).join(",")}]`;
  return `{${Object.entries(v as Record<string, unknown>)
    .filter(([, value]) => value !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([k, value]) => `${JSON.stringify(k)}:${stablePayload(value)}`)
    .join(",")}}`;
}

/** Two payloads that say the same thing, whatever order they were built in. */
export function samePayload(a: unknown, b: unknown): boolean {
  return stablePayload(a) === stablePayload(b);
}

function payloadKeyCount(v: unknown): number {
  if (v === null || typeof v !== "object") return 0;
  return Array.isArray(v) ? v.length : Object.keys(v).length;
}

function rankSpan(r: RewardSpec): { from: number; to: number } | null {
  if (!isNum(r.rank_from) || !isNum(r.rank_to)) return null;
  return { from: r.rank_from, to: r.rank_to };
}

/** Was this band paid for by anybody in the old table? The note the mockup
   pins to a new row depends on the answer, and overlap is the question — a
   band carved out of a wider prize is not "a range nobody was paid for". */
function overlapsAny(row: RewardSpec, table: readonly RewardSpec[]): boolean {
  const span = rankSpan(row);
  if (!span) return true;
  return table.some((other) => {
    const o = rankSpan(other);
    return o !== null && o.from <= span.to && span.from <= o.to;
  });
}

/**
 * What the replacement does to the table on the board.
 *
 * Rows the two tables agree on are left out entirely: the diff is read to
 * decide whether to schedule, and a list where most lines say "unchanged"
 * hides the handful that do not. Kept and added rows come in the order the
 * REPLACEMENT lists them — that is the table the operator built and the order
 * they built it in — and the removed ones follow at the end, since the new
 * table has no place to put them.
 */
export function diffRewardTables(
  before: readonly RewardSpec[],
  after: readonly RewardSpec[],
): RewardsTableDiff {
  const old = new Map<string, RewardSpec>();
  for (const r of before) old.set(rewardIdentity(r), r);
  const kept = new Set<string>();

  const rows: RewardDiffRow[] = [];
  let added = 0;
  let changed = 0;

  for (const row of after) {
    const key = rewardIdentity(row);
    const previous = old.get(key);
    if (previous) {
      kept.add(key);
      const payloadHeld = samePayload(previous.payload, row.payload);
      const sameName = (previous.name ?? "") === (row.name ?? "");
      if (payloadHeld && sameName) continue;
      changed += 1;
      rows.push({
        change: "changed",
        range: rewardRangeLabel(row),
        oldPayload: rewardPayloadText(previous.payload),
        newPayload: rewardPayloadText(row.payload),
        note: !sameName
          ? `renamed “${previous.name}” → “${row.name}”`
          : payloadKeyCount(row.payload) > payloadKeyCount(previous.payload)
            ? "same range, richer payload"
            : undefined,
        key,
      });
      continue;
    }
    added += 1;
    rows.push({
      change: "added",
      range: rewardRangeLabel(row),
      newPayload: rewardPayloadText(row.payload),
      note: overlapsAny(row, before)
        ? undefined
        : "a range nobody was paid for before",
      key,
    });
  }

  const gone = before.filter((r) => !kept.has(rewardIdentity(r)));
  // A percentile row leaving while rank ranges arrive is the one removal that
  // is not a removal: the band is still paid for, by rows that name it in
  // ranks. The mockup says so on that row, and nowhere else.
  const rankRangesArrived = rows.some(
    (r) => r.change === "added" && !r.range.endsWith("%"),
  );
  for (const row of gone) {
    const percentile = isNum(row.percentile_from) && isNum(row.percentile_to);
    rows.push({
      change: "removed",
      range: rewardRangeLabel(row),
      oldPayload: rewardPayloadText(row.payload),
      note:
        percentile && rankRangesArrived
          ? "percentile row dropped in favour of the rank ranges above"
          : undefined,
      key: rewardIdentity(row),
    });
  }

  return {
    rows,
    added,
    removed: gone.length,
    changed,
    beforeCount: before.length,
    afterCount: after.length,
  };
}

/** "3 added · 1 removed · 2 changed" — the group header's own tally, with the
   kinds that did not happen left out rather than printed as zero. */
export function summariseRewardsDiff(diff: RewardsTableDiff): string {
  const parts: string[] = [];
  if (diff.added > 0) parts.push(`${diff.added} added`);
  if (diff.removed > 0) parts.push(`${diff.removed} removed`);
  if (diff.changed > 0) parts.push(`${diff.changed} changed`);
  return parts.join(" · ");
}

/** "12 rows → 14 rows" — the badge beside the group heading. */
export function rewardsCountLabel(diff: RewardsTableDiff): string {
  const rows = (n: number) => (n === 1 ? "1 row" : `${n} rows`);
  return `${rows(diff.beforeCount)} → ${rows(diff.afterCount)}`;
}
